import { AsyncThunk, Dispatch } from '@reduxjs/toolkit';
import usePrevious from 'hooks/usePrevious';
import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { RequestStatus, REQUEST_STATUSES } from './common';
import type { RootState, AppDispatch } from './store';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

interface AsyncThunkConfig {
  state?: unknown;
  dispatch?: Dispatch;
  extra?: unknown;
  rejectValue?: unknown;
  serializedErrorType?: unknown;
  pendingMeta?: unknown;
  fulfilledMeta?: unknown;
  rejectedMeta?: unknown;
}

export function useStoreData<T, U = void>(
  dataGetter: (state: RootState) => T,
  requestStatusGetter: (state: RootState) => RequestStatus,
  thunk: AsyncThunk<any, U, AsyncThunkConfig>,
  params?: U,
): [T, RequestStatus] {
  const dispatch = useAppDispatch();
  const data = useAppSelector(dataGetter);
  const requestStatus = useAppSelector(requestStatusGetter);
  const previousParams = usePrevious(params);
  const paramsChanged = !isEqual(params, previousParams);

  useEffect(() => {
    if (requestStatus === REQUEST_STATUSES.NOT_INITIALIZED || paramsChanged) {
      dispatch(thunk(params as U));
    }
  }, [requestStatus, params, thunk, paramsChanged, dispatch]);

  return [data, requestStatus];
}
